import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { PMREMGenerator, type WebGPURenderer } from 'three/webgpu';
import { sun, useSunStore } from './sun';

// Three emissive panels in a black room, prefiltered once: the reflections metallic MEP shows,
// without an HDRI fetch.
const PANELS: {
  color: string;
  intensity: number;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number];
}[] = [
  {
    color: '#e8ecf4',
    intensity: 1.5,
    position: [0, 60, 0],
    rotation: [Math.PI / 2, 0, 0],
    scale: [120, 120],
  },
  {
    color: '#ffe9c4',
    intensity: 1.2,
    position: [60, 20, 40],
    rotation: [0, -Math.PI / 3, 0],
    scale: [60, 20],
  },
  {
    color: '#a8d4ff',
    intensity: 0.8,
    position: [-80, 12, -20],
    rotation: [0, Math.PI / 2.5, 0],
    scale: [50, 16],
  },
];

function roomScene(): THREE.Scene {
  const scene = new THREE.Scene();
  for (const p of PANELS) {
    const c = new THREE.Color(p.color).multiplyScalar(p.intensity);
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(p.scale[0], p.scale[1]),
      new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide }),
    );
    mesh.position.set(...p.position);
    mesh.rotation.set(...p.rotation);
    mesh.lookAt(0, 0, 0);
    scene.add(mesh);
  }
  return scene;
}

function Sun() {
  const ref = useRef<THREE.DirectionalLight>(null);
  const get = useThree((s) => s.get);
  useEffect(() => {
    const { gl } = get();
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFShadowMap;
    return () => {
      gl.shadowMap.enabled = false;
    };
  }, [get]);
  useFrame(() => {
    const light = ref.current;
    if (!light) return;
    const { radius } = sun;
    light.position.copy(sun.target).addScaledVector(sun.direction, radius * 2);
    light.target.position.copy(sun.target);
    light.target.updateMatrixWorld();
    light.intensity = sun.intensity;
    light.color.copy(sun.color);
    const cam = light.shadow.camera;
    if (cam.right !== radius) {
      cam.left = cam.bottom = -radius;
      cam.right = cam.top = radius;
      cam.near = 1;
      cam.far = radius * 4;
      cam.updateProjectionMatrix();
    }
  });
  return (
    <directionalLight
      ref={ref}
      castShadow
      shadow-mapSize={[2048, 2048]}
      shadow-bias={-0.0005}
      shadow-normalBias={0.05}
      shadow-radius={3}
    />
  );
}

export function Lights() {
  const get = useThree((s) => s.get);
  const sunOn = useSunStore((s) => s.on);
  useEffect(() => {
    get().scene.environmentIntensity = sunOn ? 0.35 : 1;
  }, [get, sunOn]);
  useEffect(() => {
    const { gl, scene } = get();
    const pmrem = new PMREMGenerator(gl as unknown as WebGPURenderer);
    const room = roomScene();
    const target = pmrem.fromScene(room, 0, 0.1, 1000);
    scene.environment = target.texture;
    scene.environmentIntensity = 1;
    return () => {
      scene.environment = null;
      target.dispose();
      pmrem.dispose();
      room.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          m.geometry.dispose();
          (m.material as THREE.Material).dispose();
        }
      });
    };
  }, [get]);
  return (
    <>
      <hemisphereLight intensity={sunOn ? 0.25 : 0.5} color="#cfd8e3" groundColor="#1a1c22" />
      <directionalLight position={[40, 80, 30]} intensity={1.1} color="#fff4e0" visible={!sunOn} />
      <directionalLight position={[-60, 30, -40]} intensity={sunOn ? 0.15 : 0.45} color="#9fc9ff" />
      {sunOn && <Sun />}
    </>
  );
}
